import * as React from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { NotConfiguredBanner } from "@/components/common/NotConfiguredBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/supabase/AuthContext";
import { createBrowserSupabase } from "@/supabase/client";
import { explainWithAI, transcribeWithAI } from "@/lib/ai";
import { isValidMonthKey } from "@/lib/month";
import { parseCurrencyAmount, formatCurrencyAmount } from "@/lib/currency";
import { buildUnambiguousUnitCosts, buildWeightedAverageInvoiceCosts, createApprovedProductResolver, createApprovedVariantResolver, isConsumableInvoiceNumber, isDirectProfitService, resolveVariantFirstUnitCost, type ApprovedProductMapping, type SourceProductIdentity } from "@/features/nebim/productReconciliation";
import { matchInvoiceProduct } from "@/features/besiktas/invoiceProductMatching";
import { AlertCircle, RefreshCw, TrendingUp, TrendingDown, Sparkles, Target, Package, CircleDollarSign, Loader2, ShoppingBag, Wallet, Percent, CalendarDays, LogOut, Mic, Volume2 } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from "recharts";

type Batch = {
  id: string;
  original_filename: string | null;
  status: string;
  import_type: string | null;
  created_at: string;
  source_period_start?: string | null;
  source_period_end?: string | null;
  error_summary: unknown;
};
type Sales = {
  sku: string | null; product_name: string | null; barcode: string | null; color_code?: string | null; size?: string | null;
  net_qty: number | null; gross_qty: number | null; return_qty: number | null;
  net_sales_vh: number | null; net_sales_vd: number | null;
};
type Stock = { sku: string | null; product_name: string | null; barcode?: string | null; color_code?: string | null; size?: string | null; inventory_qty: number | null; available_qty: number | null; cost_price: number | null; cost_total: number | null; store_name: string | null; };
type DataRow<T> = { id: string; row_status: string; normalized_payload: T | null; source_row_number: number; };
type ProductMetric = { key: string; costKey: string; fallbackCostKey: string | null; sku: string; name: string; qty: number; revenue: number; revenueExVat: number; };
type DailyRevenue = { sourceDate: string; revenue: number; revenueExVat: number; units: number; usedVatFallback: boolean; calculatedVatRows: number; };
type InvoiceVatLine = { source_product_code: string | null; vat_rate: number | null; product_id?: string | null };
type ProductVariantCode = { product_id: string; product_code: string };
type InvoiceStockCostPosting = { product_id: string | null; variant_id: string | null; quantity: number | string; unit_cost: number | string };
type ExpenseRow = { amount: number | string; category: string | null; expense_date: string };

// No manually invented/proxy product costs are permitted in profitability calculations.
const ANALYSIS_ESTIMATE_BY_SKU = new Map<string, any>();

const PAGE_SIZE = 1000;
const money = (n: number | null | undefined) => formatCurrencyAmount(n, 2);
const number = (n: number | null | undefined) => n == null || !Number.isFinite(n) ? "—" : new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(n);
async function loadRows<T>(client: any, companyId: string, batchId: string): Promise<DataRow<T>[]> {
  const result: DataRow<T>[] = [];
  for (let start = 0; start < 20000; start += PAGE_SIZE) {
    const { data, error } = await client.from("import_rows")
      .select("id, row_status, normalized_payload, source_row_number")
      .eq("company_id", companyId).eq("batch_id", batchId)
      .order("source_row_number", { ascending: true }).range(start, start + PAGE_SIZE - 1);
    if (error) throw error;
    const page = (data ?? []) as DataRow<T>[];
    result.push(...page);
    if (page.length < PAGE_SIZE) return result;
  }
  // Never present capped rows as complete financial totals.
  throw new Error("Analiz aktarımı 20.000 satır sınırına ulaştı. Toplamlar eksik kalabileceği için analiz durduruldu; aktarımı bölerek yeniden yükleyin.");
}

const Dashboard: React.FC = () => {
  const { companyId, user, signOut } = useAuth();
  const factory = React.useMemo(() => createBrowserSupabase(), []);
  const [salesBatch, setSalesBatch] = React.useState<Batch | null>(null);
  const [stockBatch, setStockBatch] = React.useState<Batch | null>(null);
  const [salesRows, setSalesRows] = React.useState<DataRow<Sales>[]>([]);
  const [allSalesRows, setAllSalesRows] = React.useState<(DataRow<Sales> & { sourceDate: string })[]>([]);
  const [dailyRevenueHistory, setDailyRevenueHistory] = React.useState<DailyRevenue[]>([]);
  const [vatRatesBySku, setVatRatesBySku] = React.useState<Record<string, number | null>>({});
  const [vatRateWarning, setVatRateWarning] = React.useState<string | null>(null);
  const [stockRows, setStockRows] = React.useState<DataRow<Stock>[]>([]);
  const [approvedMappings, setApprovedMappings] = React.useState<ApprovedProductMapping[]>([]);
  const [invoiceUnitCostsByVariant, setInvoiceUnitCostsByVariant] = React.useState<Record<string, number>>({});
  const [invoiceUnitCostsByProduct, setInvoiceUnitCostsByProduct] = React.useState<Record<string, number>>({});
  const [selectedReportMonth, setSelectedReportMonth] = React.useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [targetMonth, setTargetMonth] = React.useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [targetText, setTargetText] = React.useState("");
  const [targetSaving, setTargetSaving] = React.useState(false);
  const [targetError, setTargetError] = React.useState<string | null>(null);
  const [targetNotice, setTargetNotice] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [aiText, setAiText] = React.useState("");
  const [aiError, setAiError] = React.useState<string | null>(null);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);
  const [aiInput, setAiInput] = React.useState("");
  const [aiMessages, setAiMessages] = React.useState<Array<{ role: "user" | "assistant"; text: string }>>([]);
  const [aiListening, setAiListening] = React.useState(false);
  const [aiSpeaking, setAiSpeaking] = React.useState(false);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const audioStreamRef = React.useRef<MediaStream | null>(null);
  const audioChunksRef = React.useRef<Blob[]>([]);
  const discardAudioRef = React.useRef(false);
  const [monthlyExpenseTotal, setMonthlyExpenseTotal] = React.useState<number | null>(null);
  const [monthlyExpenseBreakdown, setMonthlyExpenseBreakdown] = React.useState<{ category: string; amount: number }[]>([]);
  const [expenseError, setExpenseError] = React.useState<string | null>(null);
  const analyticsRequestId = React.useRef(0);
  const targetRequestId = React.useRef(0);
  const expenseRequestId = React.useRef(0);

  const loadAnalytics = React.useCallback(async () => {
    const requestId = ++analyticsRequestId.current;
    if (!factory.ok || !companyId) {
      // Company/session changes must not leave the previous company's figures visible.
      setSalesBatch(null);
      setStockBatch(null);
      setSalesRows([]);
      setAllSalesRows([]);
      setDailyRevenueHistory([]);
      setVatRatesBySku({});
      setVatRateWarning(null);
      setStockRows([]);
      setApprovedMappings([]);
      setInvoiceUnitCostsByVariant({});
      setInvoiceUnitCostsByProduct({});
      setAiText("");
      setAiError(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true); setError(null); setAiError(null);
    try {
      let scannedCount = 0;
      let nextSales: Batch | null = null;
      let nextStock: Batch | null = null;
      const BATCH_PAGE_SIZE = 500;
      const BATCH_SCAN_CAP = 10000;
      let scannedAllAvailable = false;
      for (let start = 0; start < BATCH_SCAN_CAP; start += BATCH_PAGE_SIZE) {
        const { data, error: batchError } = await factory.client.from("import_batches")
          .select("id, original_filename, status, import_type, created_at, source_period_start, source_period_end, error_summary")
          .eq("company_id", companyId).eq("status", "committed")
          .in("import_type", ["nebim_sales_daily", "nebim_stock_snapshot"])
          .order("created_at", { ascending: false })
          .range(start, start + BATCH_PAGE_SIZE - 1);
        if (requestId !== analyticsRequestId.current) return;
        if (batchError) throw batchError;
        const page = (data ?? []) as Batch[];
        scannedCount += page.length;
        for (const batch of page) {
          if (!nextSales && batch.import_type === "nebim_sales_daily") nextSales = batch;
          if (!nextStock && batch.import_type === "nebim_stock_snapshot") nextStock = batch;
        }
        if (nextSales && nextStock) break;
        if (page.length < BATCH_PAGE_SIZE) {
          scannedAllAvailable = true;
          break;
        }
      }
      if (!scannedAllAvailable && scannedCount >= BATCH_SCAN_CAP && (!nextSales || !nextStock)) {
        throw new Error("Son 10.000 aktarım kaydı tarandı ancak satış ve stok raporlarının ikisi birden bulunamadı. Eksik geçmiş veriyi varmış gibi göstermemek için analiz durduruldu.");
      }
      const [sRows, stRows] = await Promise.all([
        nextSales ? loadRows<Sales>(factory.client, companyId, nextSales.id) : Promise.resolve([]),
        nextStock ? loadRows<Stock>(factory.client, companyId, nextStock.id) : Promise.resolve([]),
      ]);

      // Use VAT rates from finalized real supplier invoices when Nebim's VD is blank.
      const taxRates: Record<string, number | null> = {};
      let taxWarning: string | null = null;
      try {
        const { data: invoiceRows, error: invoiceError } = await factory.client.from("supplier_invoices")
          .select("id").eq("company_id", companyId).eq("status", "finalized").eq("test_only", false);
        if (invoiceError) throw invoiceError;
        const invoiceIds = (invoiceRows ?? []).map((item: { id: string }) => item.id);
        const rateSets = new Map<string, Set<number>>();
        const productRateSets = new Map<string, Set<number>>();
        if (invoiceIds.length) {
          for (let offset = 0; offset < 50000; offset += PAGE_SIZE) {
            const { data, error: lineError } = await factory.client.from("supplier_invoice_lines")
              .select("source_product_code, vat_rate, product_id").eq("company_id", companyId)
              .in("invoice_id", invoiceIds).range(offset, offset + PAGE_SIZE - 1);
            if (lineError) throw lineError;
            const page = (data ?? []) as InvoiceVatLine[];
            for (const line of page) {
              const sku = (line.source_product_code ?? "").trim().toLocaleLowerCase("tr-TR");
              const rate = Number(line.vat_rate);
              if ((!sku && !line.product_id) || line.vat_rate == null || !Number.isFinite(rate) || rate < 0 || rate > 100) continue;
              if (sku) {
                const set = rateSets.get(sku) ?? new Set<number>();
                set.add(rate);
                rateSets.set(sku, set);
              }
              if (line.product_id) {
                const productSet = productRateSets.get(line.product_id) ?? new Set<number>();
                productSet.add(rate);
                productRateSets.set(line.product_id, productSet);
              }
            }
            if (page.length < PAGE_SIZE) break;
            if (offset + PAGE_SIZE >= 50000) throw new Error("Fatura KDV oranları 50.000 satır sınırına ulaştı.");
          }
        }
        if (productRateSets.size) {
          for (let offset = 0; offset < 50000; offset += PAGE_SIZE) {
            const { data, error: variantError } = await factory.client.from("product_variants")
              .select("product_id, product_code").eq("company_id", companyId)
              .range(offset, offset + PAGE_SIZE - 1);
            if (variantError) throw variantError;
            const page = (data ?? []) as ProductVariantCode[];
            for (const variant of page) {
              const rates = productRateSets.get(variant.product_id);
              const sku = (variant.product_code ?? "").trim().toLocaleLowerCase("tr-TR");
              if (!sku || !rates) continue;
              const set = rateSets.get(sku) ?? new Set<number>();
              for (const rate of rates) set.add(rate);
              rateSets.set(sku, set);
            }
            if (page.length < PAGE_SIZE) break;
            if (offset + PAGE_SIZE >= 50000) throw new Error("Ürün kataloğu KDV eşlemesi 50.000 satır sınırına ulaştı.");
          }
        }
        // Bridge invoice VAT rates to Nebim sales codes through approved variant mappings.
        // This is conservative: a source SKU is assigned a rate only when every matching
        // mapped variant resolves to one identical VAT rate.
        const variantParentById = new Map<string, string>();
        for (let offset = 0; offset < 50000; offset += PAGE_SIZE) {
          const { data, error: variantError } = await factory.client.from("product_variants")
            .select("id, product_id").eq("company_id", companyId)
            .range(offset, offset + PAGE_SIZE - 1);
          if (variantError) throw variantError;
          const page = (data ?? []) as Array<{ id: string; product_id: string }>;
          for (const variant of page) variantParentById.set(variant.id, variant.product_id);
          if (page.length < PAGE_SIZE) break;
          if (offset + PAGE_SIZE >= 50000) throw new Error("Ürün varyantları KDV eşlemesi 50.000 satır sınırına ulaştı.");
        }
        const mappedSkuRates = new Map<string, Set<number>>();
        for (let offset = 0; offset < 50000; offset += PAGE_SIZE) {
          const { data, error: mappingError } = await factory.client.from("product_mappings")
            .select("source_product_code, product_variant_id")
            .eq("company_id", companyId).eq("source_system", "nebim").eq("status", "approved")
            .range(offset, offset + PAGE_SIZE - 1);
          if (mappingError) throw mappingError;
          const page = (data ?? []) as Array<{ source_product_code: string | null; product_variant_id: string | null }>;
          for (const mapping of page) {
            const sku = (mapping.source_product_code ?? "").trim().toLocaleLowerCase("tr-TR");
            const parentId = mapping.product_variant_id ? variantParentById.get(mapping.product_variant_id) : undefined;
            const rates = parentId ? productRateSets.get(parentId) : undefined;
            if (!sku || !rates) continue;
            const set = mappedSkuRates.get(sku) ?? new Set<number>();
            for (const rate of rates) set.add(rate);
            mappedSkuRates.set(sku, set);
          }
          if (page.length < PAGE_SIZE) break;
          if (offset + PAGE_SIZE >= 50000) throw new Error("Ürün eşleştirmeleri KDV çözümlemesi 50.000 satır sınırına ulaştı.");
        }
        for (const [sku, rates] of mappedSkuRates) {
          const existing = rateSets.get(sku) ?? new Set<number>();
          for (const rate of rates) existing.add(rate);
          rateSets.set(sku, existing);
        }
        for (const [sku, rates] of rateSets) taxRates[sku] = rates.size === 1 ? [...rates][0] : null;
      } catch (taxError) {
        const detail = taxError as { message?: unknown };
        taxWarning = "Faturalardan KDV oranları okunamadı; eksik VD değerleri için KDV dahil ciro hesaplanmadı." +
          (typeof detail?.message === "string" ? " " + detail.message : "");
      }

      // Aggregate committed reports by their source date, never by upload date.
      // Keep only the newest committed report for each date to avoid double counting.
      const historyBatches: Batch[] = [];
      let historyComplete = false;
      for (let offset = 0; offset < 10000; offset += BATCH_PAGE_SIZE) {
        const { data, error: historyError } = await factory.client.from("import_batches")
          .select("id, original_filename, status, import_type, created_at, source_period_start, source_period_end, error_summary")
          .eq("company_id", companyId).eq("status", "committed").eq("import_type", "nebim_sales_daily")
          .not("source_period_start", "is", null)
          .order("created_at", { ascending: false })
          .range(offset, offset + BATCH_PAGE_SIZE - 1);
        if (requestId !== analyticsRequestId.current) return;
        if (historyError) throw historyError;
        const page = (data ?? []) as Batch[];
        historyBatches.push(...page);
        if (page.length < BATCH_PAGE_SIZE) { historyComplete = true; break; }
      }
      if (!historyComplete) throw new Error("10.000 tarihli satış aktarımı sınırına ulaşıldı. Eksik toplam göstermemek için geçmiş ciro hesabı durduruldu.");
      const newestBatchByDate = new Map<string, Batch>();
      for (const item of historyBatches) {
        if (item.source_period_start && !newestBatchByDate.has(item.source_period_start)) newestBatchByDate.set(item.source_period_start, item);
      }
      const history: DailyRevenue[] = [];
      const allHistoryRows: (DataRow<Sales> & { sourceDate: string })[] = [];
      for (const item of newestBatchByDate.values()) {
        const historicalRows = item.id === nextSales?.id ? sRows : await loadRows<Sales>(factory.client, companyId, item.id);
        if (requestId !== analyticsRequestId.current) return;
        let revenue = 0, revenueExVat = 0, units = 0, usedVatFallback = false, calculatedVatRows = 0;
        for (const row of historicalRows) {
          if (row.row_status === "error" || !row.normalized_payload) continue;
          allHistoryRows.push({ ...row, sourceDate: item.source_period_start! });
          const sale = row.normalized_payload;
          const vh = Number(sale.net_sales_vh ?? 0);
          const vd = Number(sale.net_sales_vd ?? 0);
          const sku = (sale.sku ?? "").trim().toLocaleLowerCase("tr-TR");
          const rate = taxRates[sku];
          let includingVat = vd !== 0 ? vd : vh;
          if (vd === 0 && vh !== 0 && rate != null) {
            includingVat = Math.round((vh * (1 + rate / 100) + Number.EPSILON) * 100) / 100;
            calculatedVatRows++;
          } else if (vd === 0 && vh !== 0) {
            usedVatFallback = true;
          }
          revenue += includingVat;
          revenueExVat += vh;
          units += Number(sale.net_qty ?? 0);
        }
        history.push({ sourceDate: item.source_period_start!, revenue, revenueExVat, units, usedVatFallback, calculatedVatRows });
      }
      history.sort((a, b) => a.sourceDate.localeCompare(b.sourceDate));
      // Load every approved mapping in bounded pages. Partial mapping lists could
      // make an ambiguous variant look unique, so fail closed at the hard limit.
      const mappings: ApprovedProductMapping[] = [];
      let mappingsComplete = false;
      for (let offset = 0; offset < 100000; offset += PAGE_SIZE) {
        const { data, error: mappingError } = await factory.client.from("product_mappings")
          .select("source_product_code, source_product_name, source_color_code, source_size_code, source_barcode, product_variant_id, product_id, status")
          .eq("company_id", companyId).eq("source_system", "nebim").eq("status", "approved")
          .order("updated_at", { ascending: false }).range(offset, offset + PAGE_SIZE - 1);
        if (requestId !== analyticsRequestId.current) return;
        if (mappingError) throw mappingError;
        const page = (data ?? []) as ApprovedProductMapping[];
        mappings.push(...page);
        if (page.length < PAGE_SIZE) { mappingsComplete = true; break; }
      }
      if (!mappingsComplete) throw new Error("100.000 onaylı ürün eşlemesi sınırına ulaşıldı. Eksik eşlemeyle maliyet hesabı yapılmadı.");

      // Use real invoice line costs even while an invoice is still a draft.
      // Cost analysis must not require stock finalization or cari transfer.
      // supplier_invoice_lines is the single source here, so finalized postings
      // are deliberately not added again (prevents duplicate purchase costs).
      const invoiceCostPostings: InvoiceStockCostPosting[] = [];
      const { data: realInvoices, error: realInvoiceError } = await factory.client.from("supplier_invoices")
        .select("id,invoice_no")
        .eq("company_id", companyId)
        .eq("test_only", false)
        .neq("status", "void")
        .order("invoice_date", { ascending: true });
      if (realInvoiceError) throw realInvoiceError;
      const realInvoiceIds = (realInvoices ?? []).map((item: { id: string }) => item.id);
      const { data: activeCatalogProducts, error: catalogError } = await factory.client.from("products")
        .select("id,product_code,product_name").eq("company_id", companyId).eq("is_active", true);
      if (catalogError) throw catalogError;
      const productIdsByCode = new Map<string, string[]>();
      const invoiceCatalog = (activeCatalogProducts ?? []) as Array<{ id: string; product_code: string | null; product_name: string | null }>;
      const invoiceNoById = new Map((realInvoices ?? []).map((item: { id: string; invoice_no?: string | null }) => [item.id, item.invoice_no ?? null]));
      for (const product of (activeCatalogProducts ?? []) as Array<{ id: string; product_code: string | null }>) {
        const code = (product.product_code ?? "").trim().toLocaleLowerCase("tr-TR");
        if (!code) continue;
        const ids = productIdsByCode.get(code) ?? [];
        ids.push(product.id);
        productIdsByCode.set(code, ids);
      }
      if (realInvoiceIds.length) {
        let invoiceLinesComplete = true;
        for (let invoiceOffset = 0; invoiceOffset < realInvoiceIds.length; invoiceOffset += 100) {
          const invoiceChunk = realInvoiceIds.slice(invoiceOffset, invoiceOffset + 100);
          for (let offset = 0; offset < 50000; offset += PAGE_SIZE) {
            const { data, error: lineError } = await factory.client.from("supplier_invoice_lines")
              .select("invoice_id,product_id,variant_id,quantity,line_subtotal,source_product_code,product_name,raw_data")
              .eq("company_id", companyId)
              .in("invoice_id", invoiceChunk)
              .order("invoice_id", { ascending: true })
              .order("line_number", { ascending: true })
              .range(offset, offset + PAGE_SIZE - 1);
            if (lineError) throw lineError;
            const page = (data ?? []) as Array<{ invoice_id: string; product_id: string | null; variant_id: string | null; quantity: number | string | null; line_subtotal: number | string | null; source_product_code?: string | null; product_name?: string | null; raw_data?: any }>;
            for (const line of page as any[]) {
              if (isConsumableInvoiceNumber(invoiceNoById.get(line.invoice_id))) continue;
              const quantity = Number(line.quantity);
              const lineSubtotal = line.line_subtotal == null ? NaN : Number(line.line_subtotal);
              const matchedCode = String(line.raw_data?.matched_product_code ?? line.source_product_code ?? "").trim().toLocaleLowerCase("tr-TR");
              const codeMatches = productIdsByCode.get(matchedCode) ?? [];
              const aiReadyMatch = matchInvoiceProduct({ product_code: line.raw_data?.matched_product_code ?? line.source_product_code, product_name: line.product_name }, invoiceCatalog.map((product) => ({ ...product, product_code: product.product_code ?? "" })));
              const resolvedProductId = line.product_id ?? (codeMatches.length === 1 ? codeMatches[0] : aiReadyMatch?.product.id ?? null);
              // Exact unique catalog-code mapping is permitted; ambiguous codes and
              // incomplete/zero-cost lines remain uncovered rather than guessed.
              if (!resolvedProductId || !Number.isFinite(quantity) || quantity <= 0 ||
                  !Number.isFinite(lineSubtotal) || lineSubtotal <= 0) continue;
              invoiceCostPostings.push({
                product_id: resolvedProductId,
                variant_id: line.variant_id ?? line.raw_data?.matched_product_variant_id ?? null,
                quantity,
                unit_cost: lineSubtotal / quantity,
              });
            }
            if (page.length < PAGE_SIZE) break;
            if (offset + PAGE_SIZE >= 50000) {
              invoiceLinesComplete = false;
              throw new Error("Gerçek alış faturası kalemleri 50.000 satır sınırına ulaştı; eksik maliyetle kârlılık hesaplanmadı.");
            }
          }
          if (!invoiceLinesComplete) break;
        }
      }
      const invoiceCosts = buildWeightedAverageInvoiceCosts(invoiceCostPostings);
      const invoiceUnitCosts: Record<string, number> = {};
      const invoiceProductCosts: Record<string, number> = {};
      for (const [id, cost] of invoiceCosts.byVariant) invoiceUnitCosts[id] = cost;
      for (const [id, cost] of invoiceCosts.byProduct) invoiceProductCosts[id] = cost;

      if (requestId !== analyticsRequestId.current) return;
      setSalesBatch(nextSales); setStockBatch(nextStock);
      setSalesRows(sRows); setAllSalesRows(allHistoryRows); setDailyRevenueHistory(history); setStockRows(stRows); setApprovedMappings(mappings); setVatRatesBySku(taxRates); setVatRateWarning(taxWarning);
      setInvoiceUnitCostsByVariant(invoiceUnitCosts);
      setInvoiceUnitCostsByProduct(invoiceProductCosts);
      setAiText("");
    } catch (e) {
      if (requestId !== analyticsRequestId.current) return;
      const err = e as { message?: unknown };
      // Clear previously loaded values: never display stale analytics as current after a failed refresh.
      setSalesBatch(null);
      setStockBatch(null);
      setSalesRows([]);
      setAllSalesRows([]);
      setDailyRevenueHistory([]);
      setVatRatesBySku({});
      setVatRateWarning(null);
      setStockRows([]);
      setApprovedMappings([]);
      setInvoiceUnitCostsByVariant({});
      setAiText("");
      setError(typeof err?.message === "string" ? err.message : "Analiz verileri yüklenemedi.");
    } finally {
      if (requestId === analyticsRequestId.current) setLoading(false);
    }
  }, [factory, companyId]);

  React.useEffect(() => {
    void loadAnalytics();
    return () => { analyticsRequestId.current += 1; };
  }, [loadAnalytics]);

  const loadMonthlyTarget = React.useCallback(async () => {
    const requestId = ++targetRequestId.current;
    if (!factory.ok || !companyId || !isValidMonthKey(targetMonth)) {
      setTargetText("");
      setTargetError(null);
      return;
    }
    // Ay/şirket değişirken önceki aya ait hedef ekranda kalmasın.
    setTargetText("");
    setTargetError(null);
    setTargetNotice(null);
    try {
      const { data, error: queryError } = await factory.client.from("monthly_revenue_targets")
        .select("target_amount").eq("company_id", companyId).eq("target_month", `${targetMonth}-01`).maybeSingle();
      if (requestId !== targetRequestId.current) return;
      if (queryError) {
        setTargetText("");
        setTargetError(queryError.message.includes("monthly_revenue_targets")
          ? "Aylık hedef tablosu henüz veritabanında kurulmamış. Ortak hedef kaydı için PR içindeki migration gerekir."
          : `Aylık hedef yüklenemedi: ${queryError.message}`);
        return;
      }
      setTargetText(data?.target_amount == null ? "" : String(data.target_amount));
    } catch (e) {
      if (requestId !== targetRequestId.current) return;
      const err = e as { message?: unknown };
      setTargetText("");
      setTargetError(`Aylık hedef yüklenemedi: ${typeof err?.message === "string" ? err.message : "Bağlantı hatası"}`);
    }
  }, [factory, companyId, targetMonth]);

  React.useEffect(() => {
    void loadMonthlyTarget();
    return () => { targetRequestId.current += 1; };
  }, [loadMonthlyTarget]);

  const loadMonthlyExpenses = React.useCallback(async () => {
    const requestId = ++expenseRequestId.current;
    if (!factory.ok || !companyId || !isValidMonthKey(selectedReportMonth)) {
      setMonthlyExpenseTotal(null);
      setMonthlyExpenseBreakdown([]);
      setExpenseError(null);
      return;
    }
    // Eski aya ait toplam, yeni ay yüklenirken gösterilmesin.
    setMonthlyExpenseTotal(null);
    setMonthlyExpenseBreakdown([]);
    setExpenseError(null);
    const start = `${selectedReportMonth}-01`;
    const [year, month] = selectedReportMonth.split("-").map(Number);
    const end = `${year + (month === 12 ? 1 : 0)}-${String(month === 12 ? 1 : month + 1).padStart(2, "0")}-01`;
    try {
      let total = 0;
      const grouped = new Map<string, number>();
      for (let offset = 0; offset < 100000; offset += PAGE_SIZE) {
        const { data, error: queryError } = await factory.client.from("operating_expenses")
          .select("amount, category, expense_date").eq("company_id", companyId)
          .gte("expense_date", start).lt("expense_date", end)
          .order("expense_date", { ascending: true }).range(offset, offset + PAGE_SIZE - 1);
        if (requestId !== expenseRequestId.current) return;
        if (queryError) throw queryError;
        const page = (data ?? []) as ExpenseRow[];
        total += page.reduce((sum, row) => sum + Number(row.amount || 0), 0);
        for (const row of page) {
          const key = (row.category ?? "").trim() || "Diğer";
          grouped.set(key, (grouped.get(key) ?? 0) + Number(row.amount || 0));
        }
        if (page.length < PAGE_SIZE) {
          setMonthlyExpenseTotal(total);
          setMonthlyExpenseBreakdown(Array.from(grouped, ([category, amount]) => ({ category, amount })).sort((a,b)=>b.amount-a.amount));
          return;
        }
      }
      throw new Error("Seçilen ayda 100.000 gider satırı sınırına ulaşıldı; eksik toplam gösterilmemesi için hesaplama durduruldu.");
    } catch (e) {
      if (requestId !== expenseRequestId.current) return;
      const err = e as { message?: unknown };
      setMonthlyExpenseTotal(null);
      setMonthlyExpenseBreakdown([]);
      const message = typeof err?.message === "string" ? err.message : "Bilinmeyen hata";
      setExpenseError(message.includes("operating_expenses")
        ? "Gider tablosu kurulmamış; veritabanı migration'ı uygulanmalı."
        : `Aylık giderler yüklenemedi: ${message}`);
    }
  }, [factory, companyId, selectedReportMonth]);

  React.useEffect(() => {
    void loadMonthlyExpenses();
    return () => { expenseRequestId.current += 1; };
  }, [loadMonthlyExpenses]);

  const saveMonthlyTarget = async () => {
    if (!factory.ok || !companyId || !user?.id || !isValidMonthKey(targetMonth)) return;
    const amount = parseCurrencyAmount(targetText);
    if (!targetText.trim() || amount == null || !Number.isFinite(amount) || amount <= 0) { setTargetError("Seçilen ay için 0’dan büyük bir ciro hedefi girin."); return; }
    // If the selected month/company changes during the request, do not let this
    // older result overwrite the newly selected month’s form state.
    const requestId = targetRequestId.current;
    const savedMonth = targetMonth;
    const savedCompanyId = companyId;
    setTargetSaving(true); setTargetError(null); setTargetNotice(null);
    try {
      const { error: saveError } = await factory.client.from("monthly_revenue_targets").upsert({
        company_id: savedCompanyId, target_month: savedMonth + "-01", target_amount: amount, created_by: user.id, updated_at: new Date().toISOString(),
      }, { onConflict: "company_id,target_month" });
      if (requestId !== targetRequestId.current) return;
      if (saveError) setTargetError("Aylık hedef kaydedilemedi: " + saveError.message);
      else {
        setTargetText(String(amount));
        setTargetNotice(new Date(savedMonth + "-01T12:00:00").toLocaleDateString("tr-TR", { month: "long", year: "numeric" }) + " hedefi kaydedildi.");
      }
    } catch (e) {
      if (requestId === targetRequestId.current) {
        const err = e as { message?: unknown };
        setTargetError("Aylık hedef kaydedilemedi: " + (typeof err?.message === "string" ? err.message : "Bağlantı hatası"));
      }
    } finally {
      setTargetSaving(false);
    }
  };

  const metrics = React.useMemo(() => {
    const selectedMonthRows = allSalesRows.filter((r) => r.sourceDate.startsWith(selectedReportMonth + "-"));
    const validSales = selectedMonthRows.filter((r) => r.row_status !== "error" && r.normalized_payload).map((r) => r.normalized_payload as Sales);
    const validStock = stockRows.filter((r) => r.row_status !== "error" && r.normalized_payload).map((r) => r.normalized_payload as Stock);
    const mappingSkus = new Set(approvedMappings.map((mapping) =>
      (mapping.source_product_code ?? "").trim().toLocaleLowerCase("tr-TR")
    ).filter(Boolean));
    const resolveProduct = createApprovedProductResolver(approvedMappings);
    const resolveVariant = createApprovedVariantResolver(approvedMappings);
    const canonicalKey = (row: SourceProductIdentity, kind: "sales" | "stock") => {
      // Keep dashboard product aggregation at parent-product level; exact
      // variant identity is applied separately when each sale's COGS is calculated.
      const productId = resolveProduct(row);
      const variantId = resolveVariant(row);
      if (productId) return "product:" + productId;
      if (variantId) return "variant:" + variantId;
      const sku = (row.sku ?? "").trim();
      // If a mapping exists but the row lacks enough identity or conflicts with
      // its barcode, never fall back to SKU-only costing for that ambiguous SKU.
      if (sku && mappingSkus.has(sku.toLocaleLowerCase("tr-TR"))) return "unresolved-" + kind + ":" + sku;
      if (sku) return "sku:" + sku;
      const barcode = (row.barcode ?? "").trim();
      return barcode ? "barcode:" + barcode : null;
    };
    const byKey = new Map<string, ProductMetric>();
    for (const s of validSales) {
      const sku = (s.sku ?? "").trim();
      const key = canonicalKey(s, "sales") ?? "unidentified-sales:" + (s.product_name ?? "unknown");
      const productId = resolveProduct(s);
      const current = byKey.get(key) ?? { key, costKey: key, fallbackCostKey: productId ? "product:" + productId : null, sku: sku || s.barcode || "—", name: s.product_name?.trim() || "Ürün adı yok", qty: 0, revenue: 0, revenueExVat: 0 };
      current.qty += Number(s.net_qty ?? 0);
      const vh = Number(s.net_sales_vh ?? 0);
      const vd = Number(s.net_sales_vd ?? 0);
      const rate = vatRatesBySku[sku.toLocaleLowerCase("tr-TR")];
      const inclusiveRevenue = vd !== 0 ? vd : (vh !== 0 && rate != null
        ? Math.round((vh * (1 + rate / 100) + Number.EPSILON) * 100) / 100
        : vh);
      current.revenue += inclusiveRevenue;
      current.revenueExVat += vh;
      if (current.name === "Ürün adı yok" && s.product_name) current.name = s.product_name;
      byKey.set(key, current);
    }
    const products = Array.from(byKey.values());
    const top = [...products].filter((p) => p.qty > 0).sort((a,b) => b.qty - a.qty || b.revenueExVat - a.revenueExVat || a.name.localeCompare(b.name, "tr-TR"));
    const slow = [...products].filter((p) => p.qty > 0).sort((a,b) => a.qty - b.qty || a.revenue - b.revenue);
    const soldKeys = new Set(products.filter((p) => p.qty > 0).map((p) => p.costKey));
    const stockWithKeys = validStock.map((stock) => ({ stock, key: canonicalKey(stock, "stock") }));
    const unsoldStockAll = stockWithKeys.filter(({ stock, key }) =>
      Number(stock.available_qty ?? stock.inventory_qty ?? 0) > 0 && (stock.sku || stock.product_name) && key && !soldKeys.has(key)
    ).map(({ stock }) => stock);
    const unsoldStock = unsoldStockAll.slice(0, 8);
    // Costs are usable only when every row resolving to the same canonical
    // identity agrees on one positive finite unit cost. Conflicts are excluded.
    const stockCostByKey = buildUnambiguousUnitCosts(
      stockWithKeys,
      ({ key }) => key,
      ({ stock }) => stock.cost_price == null ? null : Number(stock.cost_price),
    );
    const stockVariantCostByKey = buildUnambiguousUnitCosts(
      validStock,
      (stock) => {
        const variantId = resolveVariant(stock);
        return variantId ? "variant:" + variantId : null;
      },
      (stock) => stock.cost_price == null ? null : Number(stock.cost_price),
    );
    // Real invoice costs are primary. Nebim snapshot costs are fallbacks only.
    const effectiveCostByKey = new Map<string, number>();
    for (const [variantId, unitCost] of Object.entries(invoiceUnitCostsByVariant)) {
      effectiveCostByKey.set("variant:" + variantId, unitCost);
    }
    for (const [productId, unitCost] of Object.entries(invoiceUnitCostsByProduct)) {
      effectiveCostByKey.set("product:" + productId, unitCost);
    }
    for (const [key, unitCost] of stockVariantCostByKey) {
      if (!effectiveCostByKey.has(key)) effectiveCostByKey.set(key, unitCost);
    }
    for (const [key, unitCost] of stockCostByKey) {
      if (!effectiveCostByKey.has(key)) effectiveCostByKey.set(key, unitCost);
    }
    // Calculate COGS from each sale's exact variant cost where available.
    // Product-level weighted average is a fallback, not a replacement for variant costs.
    const salesCostByProduct = new Map<string, { cogs: number; complete: boolean }>();
    for (const row of selectedMonthRows) {
      if (row.row_status === "error" || !row.normalized_payload) continue;
      const sale = row.normalized_payload as Sales;
      const qty = Number(sale.net_qty ?? 0);
      if (!Number.isFinite(qty) || qty === 0) continue;
      const productId = resolveProduct(sale);
      const variantId = resolveVariant(sale);
      const productKey = productId ? "product:" + productId : canonicalKey(sale, "sales") ?? "unidentified-sales:" + (sale.product_name ?? "unknown");
      const current = salesCostByProduct.get(productKey) ?? { cogs: 0, complete: true };
      if (!isDirectProfitService(sale.sku, sale.product_name)) {
        const unitCost = resolveVariantFirstUnitCost(variantId, productId, effectiveCostByKey, productKey);
        if (unitCost == null || !Number.isFinite(unitCost) || unitCost <= 0) current.complete = false;
        else current.cogs += unitCost * qty;
      }
      salesCostByProduct.set(productKey, current);
    }
    let matchedCogs = 0, matchedSales = 0;
    for (const p of products) {
      const cost = salesCostByProduct.get(p.costKey);
      if (cost?.complete && p.qty > 0) { matchedCogs += cost.cogs; matchedSales += p.revenueExVat; }
    }
    const estimatedCostProducts = products.flatMap((p) => {
      if (p.qty <= 0) return [];
      const actualCost = effectiveCostByKey.get(p.costKey) ?? (p.fallbackCostKey ? effectiveCostByKey.get(p.fallbackCostKey) : undefined);
      if (actualCost != null) return [];
      const estimate = ANALYSIS_ESTIMATE_BY_SKU.get(p.sku.trim().toLocaleLowerCase("tr-TR"));
      if (!estimate) return [];
      return [{ sku: p.sku, name: p.name, qty: p.qty, unitCost: estimate.unitCost, totalCost: estimate.unitCost * p.qty,
        revenueExVat: p.revenueExVat, grossProfit: p.revenueExVat - estimate.unitCost * p.qty,
        confidence: estimate.confidence, reference: estimate.reference, note: estimate.note }];
    });
    const estimatedCogs = estimatedCostProducts.reduce((sum, item) => sum + item.totalCost, 0);
    const estimatedSales = estimatedCostProducts.reduce((sum, item) => sum + item.revenueExVat, 0);
    const revenue = products.reduce((sum,p) => sum + p.revenue, 0);
    const usedVatFallback = validSales.some((sale) => {
      const sku = (sale.sku ?? "").trim().toLocaleLowerCase("tr-TR");
      return Number(sale.net_sales_vd ?? 0) === 0 && Number(sale.net_sales_vh ?? 0) !== 0 && vatRatesBySku[sku] == null;
    });
    const revenueExVat = products.reduce((sum,p) => sum + p.revenueExVat, 0);
    const eligibleSalesExVat = products.filter((p) => p.qty > 0).reduce((sum, p) => sum + p.revenueExVat, 0);
    const units = products.reduce((sum,p) => sum + p.qty, 0);
    const unmatchedCostProductCount = products.filter((p) => p.qty > 0 && !salesCostByProduct.get(p.costKey)?.complete).length;
    const missingCostProducts = products
      .filter((p) => p.qty > 0 && !salesCostByProduct.get(p.costKey)?.complete)
      .map((p) => ({
        ...p,
        mappingStatus: mappingSkus.has(p.sku.toLocaleLowerCase("tr-TR"))
          ? "Onaylı ürün eşleşmesi var · fatura maliyeti yok"
          : "Ürün eşleştirmesi eksik",
      }))
      .sort((a, b) => b.revenueExVat - a.revenueExVat);
    const monthSalesRows = selectedMonthRows.filter((row) => row.row_status !== "error" && row.normalized_payload);
    const monthProducts = new Map<string, { qty: number; revenueExVat: number; cogs: number; complete: boolean }>();
    for (const row of monthSalesRows) {
      const sale = row.normalized_payload as Sales;
      const productId = resolveProduct(sale);
      const variantId = resolveVariant(sale);
      const key = productId ? "product:" + productId : canonicalKey(sale, "sales") ?? "unidentified-sales:" + (sale.product_name ?? "unknown");
      const current = monthProducts.get(key) ?? { qty: 0, revenueExVat: 0, cogs: 0, complete: true };
      const qty = Number(sale.net_qty ?? 0);
      current.qty += qty;
      current.revenueExVat += Number(sale.net_sales_vh ?? 0);
      if (qty !== 0 && !isDirectProfitService(sale.sku, sale.product_name)) {
        const unitCost = resolveVariantFirstUnitCost(variantId, productId, effectiveCostByKey, key);
        if (unitCost == null || !Number.isFinite(unitCost) || unitCost <= 0) current.complete = false;
        else current.cogs += unitCost * qty;
      }
      monthProducts.set(key, current);
    }
    let monthCogs = 0, monthMatchedRevenue = 0, monthUnmatchedCostProductCount = 0;
    for (const product of monthProducts.values()) {
      if (product.complete && product.qty > 0) { monthCogs += product.cogs; monthMatchedRevenue += product.revenueExVat; }
      else if (product.qty > 0) monthUnmatchedCostProductCount++;
    }
    // Missing product costs remain uncovered; never substitute proxy estimates.
    const monthEstimatedCogs = 0;
    const monthEstimatedRevenue = 0;
    const monthEstimatedCostProducts: any[] = [];
    const monthRevenueExVat = Array.from(monthProducts.values()).reduce((sum, product) => sum + product.revenueExVat, 0);
    const monthCostCoverage = monthRevenueExVat > 0 ? monthMatchedRevenue / monthRevenueExVat * 100 : null;
    const monthGrossProfitWithEstimates = monthMatchedRevenue - monthCogs;
    const topWithCost = top.map((product) => {
      const cost = salesCostByProduct.get(product.costKey);
      const cogs = cost?.complete ? cost.cogs : null;
      const unitCost = cogs != null && product.qty > 0 ? cogs / product.qty : null;
      const grossProfit = cogs != null ? product.revenueExVat - cogs : null;
      return { ...product, unitCost, cogs, grossProfit, margin: grossProfit != null && product.revenueExVat > 0 ? grossProfit / product.revenueExVat * 100 : null };
    });
    const dailyCostMap = new Map<string, { cogs: number; matchedRevenue: number }>();
    // Keep daily profitability across all imported dates for the historical comparison chart.
    // The daily table itself is filtered to the selected month at render time.
    for (const row of allSalesRows) {
      if (row.row_status === "error" || !row.normalized_payload || !row.sourceDate) continue;
      const sale = row.normalized_payload as Sales;
      const qty = Number(sale.net_qty ?? 0);
      if (!Number.isFinite(qty) || qty === 0) continue;
      const productId = resolveProduct(sale);
      const variantId = resolveVariant(sale);
      const key = canonicalKey(sale, "sales") ?? "unidentified-sales:" + (sale.product_name ?? "unknown");
      const directProfitService = isDirectProfitService(sale.sku, sale.product_name);
      const unitCost = directProfitService ? 0 : resolveVariantFirstUnitCost(variantId, productId, effectiveCostByKey, key);
      if (unitCost == null) continue;
      const day = dailyCostMap.get(row.sourceDate) ?? { cogs: 0, matchedRevenue: 0 };
      day.cogs += unitCost * qty;
      day.matchedRevenue += Number(sale.net_sales_vh ?? 0);
      dailyCostMap.set(row.sourceDate, day);
    }
    const dailyProfitability = [...dailyCostMap.entries()].map(([sourceDate, value]) => ({ sourceDate, cogs: value.cogs, grossProfit: value.matchedRevenue - value.cogs }));
    return { products, top, topWithCost, dailyProfitability, slow, unsoldStock, unsoldStockCount: unsoldStockAll.length, revenue, revenueExVat, eligibleSalesExVat, units, matchedCogs, matchedSales, estimatedGrossProfit: matchedSales - matchedCogs, estimatedCostProducts, estimatedCogs, estimatedSales, monthCogs, monthMatchedRevenue, monthRevenueExVat, monthCostCoverage, monthUnmatchedCostProductCount, monthEstimatedCostProducts, monthEstimatedCogs, monthEstimatedRevenue, monthGrossProfitWithEstimates, stockCostCount: effectiveCostByKey.size, invoiceProductCostCount: Object.keys(invoiceUnitCostsByProduct).length, invoiceVariantCostCount: Object.keys(invoiceUnitCostsByVariant).length, unmatchedCostProductCount, missingCostProducts, validSalesCount: validSales.length, validStockCount: validStock.length, usedVatFallback };
  }, [allSalesRows, stockRows, approvedMappings, vatRatesBySku, invoiceUnitCostsByVariant, invoiceUnitCostsByProduct, selectedReportMonth]);

  const enteredDataRevenue = React.useMemo(() => dailyRevenueHistory.reduce((sum, day) => sum + day.revenue, 0), [dailyRevenueHistory]);
  const monthlyDataRevenue = React.useMemo(() => dailyRevenueHistory.filter((day) => day.sourceDate.startsWith(selectedReportMonth + "-")).reduce((sum, day) => sum + day.revenue, 0), [dailyRevenueHistory, selectedReportMonth]);
  const monthlyEnteredDays = React.useMemo(() => dailyRevenueHistory.filter((day) => day.sourceDate.startsWith(selectedReportMonth + "-")).length, [dailyRevenueHistory, selectedReportMonth]);
  const selectedMonthDays = React.useMemo(() => dailyRevenueHistory.filter((day) => day.sourceDate.startsWith(selectedReportMonth + "-")), [dailyRevenueHistory, selectedReportMonth]);
  const selectedMonthRevenueExVat = selectedMonthDays.reduce((sum, day) => sum + day.revenueExVat, 0);
  const selectedMonthUnits = selectedMonthDays.reduce((sum, day) => sum + day.units, 0);
  const monthlyComparisonData = React.useMemo(() => {
    const grouped = new Map<string, { month: string; netSales: number; grossProfit: number }>();
    for (const day of dailyRevenueHistory) { const key=day.sourceDate.slice(0,7); const v=grouped.get(key)??{month:key,netSales:0,grossProfit:0}; v.netSales+=day.revenueExVat; grouped.set(key,v); }
    for (const day of metrics.dailyProfitability) { const key=day.sourceDate.slice(0,7); const v=grouped.get(key); if(v) v.grossProfit+=day.grossProfit; }
    return Array.from(grouped.values()).sort((a,b)=>a.month.localeCompare(b.month)).map(v=>({...v,label:new Date(v.month+"-01T12:00:00").toLocaleDateString("tr-TR",{month:"short",year:"2-digit"})}));
  }, [dailyRevenueHistory, metrics.dailyProfitability]);
  const selectedMonthName = new Date(selectedReportMonth+"-01T12:00:00").toLocaleDateString("tr-TR",{month:"long",year:"numeric"});

  const historyUsesVatFallback = React.useMemo(() => dailyRevenueHistory.some((day) => day.usedVatFallback), [dailyRevenueHistory]);
  const calculatedVatRows = React.useMemo(() => dailyRevenueHistory.reduce((sum, day) => sum + day.calculatedVatRows, 0), [dailyRevenueHistory]);

  const target = targetText.trim() ? parseCurrencyAmount(targetText) : null;
  const targetMonthActual = React.useMemo(() => dailyRevenueHistory.filter(day=>day.sourceDate.startsWith(targetMonth+"-")).reduce((sum,day)=>sum+day.revenue,0), [dailyRevenueHistory,targetMonth]);
  const costCoverage = metrics.eligibleSalesExVat > 0 ? Math.min(100, Math.max(0, metrics.matchedSales / metrics.eligibleSalesExVat * 100)) : 0;

  const buildAiSummary = () => ({
    currency: "TRY",
    selected_month: selectedReportMonth,
    data_scope: "dashboard-calculated aggregates and authenticated company records",
    sales_batch_available: Boolean(salesBatch),
    stock_snapshot_available: Boolean(stockBatch),
    sales_rows_count: metrics.validSalesCount,
    distinct_sold_product_count: metrics.products.length,
    net_units: metrics.units,
    net_revenue_including_vat: metrics.revenue,
    net_revenue_excluding_vat: metrics.revenueExVat,
    monthly_target_month: targetMonth,
    manually_set_monthly_revenue_target: targetMonth === selectedReportMonth ? target : null,
    target_month_actual_revenue: targetMonthActual,
    target_progress_percent: target && target > 0 && targetMonth === selectedReportMonth ? Number((targetMonthActual / target * 100).toFixed(1)) : null,
    estimated_cogs_for_cost_matched_items: metrics.matchedCogs,
    sales_ex_vat_with_matching_cost: metrics.matchedSales,
    estimated_gross_profit_only_on_cost_matched_items: metrics.estimatedGrossProfit,
    cost_coverage_percent_of_ex_vat_sales: Number(costCoverage.toFixed(1)),
    operating_expenses_available: monthlyExpenseTotal != null && !expenseError,
    selected_month_operating_expenses_total: monthlyExpenseTotal,
    top_five_sales_performance: metrics.top.slice(0,5).map(({name,qty,revenue})=>({product:name,qty,revenue})),
    slow_five_sales_performance: metrics.slow.slice(0,5).map(({name,qty,revenue})=>({product:name,qty,revenue})),
    stocked_items_not_seen_in_uploaded_sales_report_count: metrics.unsoldStockCount,
    data_warnings: [
      !salesBatch ? "Onaylı Nebim satış aktarımı yok." : null,
      !stockBatch ? "Onaylı Nebim stok aktarımı yok." : null,
      expenseError ? "Seçili ay giderleri okunamadı." : null,
      monthlyExpenseTotal == null ? "Seçili ay gider toplamı henüz doğrulanamadı." : null,
      metrics.monthCostCoverage == null ? "Seçili ay maliyet kapsamı doğrulanamadı." : null
    ].filter(Boolean),
    instructions: "Türkçe yanıtla. Hesaplanmış tutarları değiştirme ve rakam uydurma. Satış, stok, maliyet ve gider verilerinin kapsamını belirt. Net kârı yalnızca gerekli maliyet ve gider kapsamı yeterliyse değerlendir; aksi halde kısmi olduğunu açıkça söyle. Önerileri uygulanabilir ve ölçülebilir ver."
  });

  const askAi = async (question?: string) => {
    if (aiLoading) return;
    const userQuestion = (question ?? "Seçili ayın mağaza performansını analiz et. Güçlü yönleri, riskleri ve öncelikli 3 aksiyonu belirt.").trim();
    if (!userQuestion) return;
    setAiOpen(true);
    setAiLoading(true);
    setAiError(null);
    setAiInput("");
    const nextMessages = [...aiMessages, { role: "user" as const, text: userQuestion }];
    setAiMessages(nextMessages);
    const history = nextMessages.slice(-8).map((message) => `${message.role === "user" ? "KULLANICI" : "DANIŞMAN"}: ${message.text.slice(0, 1600)}`).join("\\n\\n");
    const prompt = [
      "Sen Kartalyuvası İFM mağazasının Türkçe konuşan, veri odaklı işletme danışmanısın.",
      "Kullanıcının devam eden konuşmasını dikkate al. Cevabı kısa başlıklar, bulgular, riskler ve uygulanabilir aksiyonlarla ver.",
      "Finansal hesaplamaları kendin yeniden üretme; aşağıdaki doğrulanmış dashboard göstergelerini yorumla. API tarafındaki şirket kayıtları ek bağlam olarak sağlanır.",
      "Eksik veya çelişkili veri varsa kesin konuşma. Hiçbir zaman stok, fatura, cari, hedef veya gider kaydını değiştirdiğini iddia etme.",
      `PANEL ÖZETİ:\\n${JSON.stringify(buildAiSummary(), null, 2)}`,
      `KONUŞMA GEÇMİŞİ:\\n${history}`,
      "Yalnızca son kullanıcı sorusuna cevap ver; önceki konuşmadaki bağlamı koru."
    ].join("\\n\\n");
    try {
      const response = await explainWithAI(prompt, companyId ?? "");
      if (!response.ok) {
        setAiError(response.message);
        setAiMessages((current) => current.filter((message, index) => !(index === current.length - 1 && message.role === "user" && message.text === userQuestion)));
      } else {
        setAiText(response.text);
        setAiMessages((current) => [...current, { role: "assistant", text: response.text }]);
      }
    } catch (e) {
      const err = e as { message?: unknown };
      setAiError(typeof err?.message === "string" ? err.message : "Yapay zekâ analizi tamamlanamadı.");
    } finally {
      setAiLoading(false);
    }
  };

  const startAiVoiceInput = async () => {
    if (aiListening) {
      mediaRecorderRef.current?.stop();
      return;
    }
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setAiError("Bu tarayıcı ses kaydını desteklemiyor. Güncel Safari veya Chrome kullanın ve siteye mikrofon izni verin.");
      return;
    }
    try {
      setAiError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];
      discardAudioRef.current = false;
      const mimeType = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (event) => { if (event.data.size) audioChunksRef.current.push(event.data); };
      recorder.onerror = () => {
        setAiListening(false);
        setAiError("Ses kaydı sırasında hata oluştu. Tekrar deneyin.");
        stream.getTracks().forEach((track) => track.stop());
      };
      recorder.onstop = async () => {
        setAiListening(false);
        stream.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
        if (discardAudioRef.current) { audioChunksRef.current = []; discardAudioRef.current = false; return; }
        const audio = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        audioChunksRef.current = [];
        if (!audio.size) { setAiError("Ses kaydı boş. Mikrofonu kontrol edip tekrar deneyin."); return; }
        setAiLoading(true);
        try {
          const result = await transcribeWithAI(audio, companyId ?? "");
          if (result.ok) setAiInput((current) => [current.trim(), result.text].filter(Boolean).join(" "));
          else setAiError(result.message);
        } finally {
          setAiLoading(false);
        }
      };
      recorder.start(500);
      setAiListening(true);
    } catch (error) {
      audioStreamRef.current?.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
      setAiListening(false);
      const name = error instanceof Error ? error.name : "";
      setAiError(name === "NotAllowedError" || name === "PermissionDeniedError"
        ? "Mikrofon izni verilmedi. Tarayıcı adres çubuğundan bu siteye mikrofon izni verin."
        : "Mikrofon açılamadı. Mikrofonun bağlı ve kullanılabilir olduğunu kontrol edin.");
    }
  };

  const speakAiText = (text: string) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setAiError("Bu tarayıcı sesli yanıt özelliğini desteklemiyor.");
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "tr-TR";
    utterance.rate = 1;
    utterance.onstart = () => setAiSpeaking(true);
    utterance.onend = () => setAiSpeaking(false);
    utterance.onerror = () => setAiSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  React.useEffect(() => () => {
    discardAudioRef.current = true;
    mediaRecorderRef.current?.stop?.();
    audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
  }, []);

  if (!factory.ok) return <div data-testid="page-dashboard" className="flex h-full items-center justify-center"><div className="w-full max-w-xl"><PageHeader eyebrow="YÖNETİM ANALİZİ" title="Genel Bakış" description="Mağaza performansı, satış, maliyet ve hedef analizi."/><NotConfiguredBanner /></div></div>;
  const compact = (v:number)=>new Intl.NumberFormat("tr-TR",{notation:"compact",maximumFractionDigits:1}).format(v);
  const colors=["#111827","#64748b","#2563eb","#16a34a","#f59e0b","#f97316"];
  const categoryBreakdown = (() => {
    const groups = new Map<string, number>();
    for (const product of metrics.products) {
      const name = product.name.toLocaleUpperCase("tr-TR");
      const category =
        /FORMA/.test(name) ? "Formalar" :
        /T.?SHIRT|TİŞÖRT|TISORT/.test(name) ? "T-Shirt" :
        /SWEAT|POLAR|HOODIE/.test(name) ? "Sweatshirt" :
        /EŞOFMAN|ESOFMAN|PANTOLON/.test(name) ? "Eşofman" :
        /ATKI|ŞAPKA|SAPKA|ANAHTARLIK|ÇANTA|CANTA|BAYRAK|BERE|KART/.test(name) ? "Aksesuar" : "Diğer";
      groups.set(category, (groups.get(category) ?? 0) + product.qty);
    }
    return [...groups.entries()].map(([category, qty]) => ({ category, qty })).sort((a,b) => b.qty-a.qty);
  })();
  
  const targetProgress=target&&target>0?Math.min(100,targetMonthActual/target*100):null;
  const latestDays=dailyRevenueHistory.filter(day=>day.sourceDate.startsWith(selectedReportMonth+"-")).sort((a,b)=>b.sourceDate.localeCompare(a.sourceDate));
  const lastDataUpdate=[salesBatch?.created_at,stockBatch?.created_at].filter((value):value is string=>Boolean(value)).sort().slice(-1)[0];
  const dashboardCards=[
    {label:"Net Satış (KDV Hariç)",value:money(selectedMonthRevenueExVat),hint:selectedMonthDays.length?"Seçilen ay":"Bu ay için satış yok",icon:ShoppingBag},
    {label:"Satış (KDV Dâhil)",value:money(monthlyDataRevenue),hint:selectedMonthDays.length?"Seçilen ay":"Bu ay için satış yok",icon:CircleDollarSign},
    {label:"Satılan Ürün Adedi",value:number(selectedMonthDays.length?selectedMonthUnits:metrics.units),hint:"Net adet · iadeler düşülmüş",icon:Package},
    {label:"Ürün Maliyeti",value:money(metrics.monthCogs),hint:metrics.monthRevenueExVat>0?`Seçilen ay · ${metrics.invoiceProductCostCount} toplam ürün maliyeti kaydı`:`Bu ay satış maliyeti yok · toplam ${metrics.invoiceProductCostCount} ürün maliyeti kaydı`,icon:Wallet},
    {label:"Brüt Kâr",value:metrics.monthCostCoverage!=null && metrics.monthMatchedRevenue>0?money(metrics.monthMatchedRevenue-metrics.monthCogs):"—",hint:metrics.monthCostCoverage!=null?`Eşleşen ürünler · maliyet kapsamı %${metrics.monthCostCoverage.toLocaleString("tr-TR",{maximumFractionDigits:1})} · ${metrics.monthUnmatchedCostProductCount} ürün eksik`:"Maliyet verisi bulunamadı",icon:Percent},
    {label:"Net Kâr (Kısmi)",value:monthlyExpenseTotal!=null && !expenseError && metrics.monthMatchedRevenue+metrics.monthEstimatedRevenue>0?money(metrics.monthGrossProfitWithEstimates-monthlyExpenseTotal):"—",hint:expenseError?"Gider verisi yüklenemedi":monthlyExpenseTotal==null?"Aylık giderler yükleniyor":`Maliyet kapsamı %${Math.min(100,metrics.monthRevenueExVat>0?(metrics.monthMatchedRevenue+metrics.monthEstimatedRevenue)/metrics.monthRevenueExVat*100:0).toLocaleString("tr-TR",{maximumFractionDigits:1})} · eksik maliyetli ürünler hariç`,icon:TrendingUp},
  ];
  return <div data-testid="page-dashboard" className="executive-dashboard">
    <header className="flex min-h-0 items-center justify-between gap-3">
      <div className="min-w-0">
        <h1 className="truncate text-[22px] font-bold leading-tight tracking-tight text-slate-950">Genel Bakış</h1>
        <p className="truncate text-[11px] text-slate-500">Mağazanın güncel performansı ve önemli göstergeler</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button size="sm" onClick={()=>setAiOpen(true)} disabled={loading} className="h-8 gap-1 px-2 text-[11px]"><Sparkles className="h-3.5 w-3.5"/>AI Danışman</Button>
        <label className="hidden text-[10px] font-semibold text-slate-500 sm:block" htmlFor="dashboard-month">DÖNEM</label>
        <Input id="dashboard-month" aria-label="Rapor ayı" type="month" value={selectedReportMonth} onChange={e=>setSelectedReportMonth(e.target.value)} className="h-8 w-[132px] border-slate-200 bg-white px-2 text-[11px]"/>
        <Button variant="outline" size="sm" onClick={()=>void loadAnalytics()} disabled={loading} className="h-8 gap-1 px-2 text-[11px]"><RefreshCw className={`h-3.5 w-3.5 ${loading?"animate-spin":""}`}/>Yenile</Button>
        <div className="hidden items-center gap-1.5 pl-1 text-[9px] text-slate-500 xl:flex"><span className="h-2 w-2 rounded-full bg-emerald-500"/>{lastDataUpdate?`Son veri aktarımı: ${new Date(lastDataUpdate).toLocaleString("tr-TR",{dateStyle:"short",timeStyle:"short"})}`:"Veri aktarım zamanı yok"}</div>
        <Button variant="outline" size="sm" onClick={()=>void signOut()} className="h-8 gap-1 px-2 text-[11px]" aria-label="Çıkış yap"><LogOut className="h-3.5 w-3.5"/>Çıkış</Button>
      </div>
    </header>

    {(error||vatRateWarning||metrics.usedVatFallback||historyUsesVatFallback||aiError)?<div className="flex min-h-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] text-amber-900" role="status">
      {error?<span role="alert">{error}</span>:null}
      {vatRateWarning?<span>{vatRateWarning}</span>:null}
      {(metrics.usedVatFallback||historyUsesVatFallback)?<span>KDV dâhil tutarı doğrulanamayan satışlar için tahmin üretilmedi.</span>:null}
      {aiError?<span role="alert">{aiError}</span>:null}
    </div>:null}

    <section aria-label="Temel performans göstergeleri" className="grid min-h-0 grid-cols-2 gap-2 lg:grid-cols-6">
      {dashboardCards.map(card=><article key={card.label} className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 shadow-[0_1px_3px_rgba(15,23,42,.05)]">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-slate-50 text-slate-800"><card.icon className="h-3.5 w-3.5"/></span>
        <div className="min-w-0"><div className="truncate text-[10px] font-semibold text-slate-500">{card.label}</div><div className="truncate text-[15px] font-bold leading-5 tracking-tight text-slate-950 tabular-nums">{loading?"…":card.value}</div><div className="truncate text-[9px] text-slate-400">{card.hint}</div></div>
      </article>)}
    </section>

    <section className="grid min-h-0 grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1.7fr)_minmax(260px,1fr)]">
      <article className="dashboard-panel min-h-0">
        <div className="flex items-center justify-between gap-2"><div><h2 className="dashboard-title">Satış ve Kâr Performansı</h2><p className="dashboard-subtitle">Günlük net satış ve KDV dâhil satış</p></div><span className="rounded border border-slate-200 px-2 py-1 text-[9px] text-slate-500">{selectedMonthName}</span></div>
        <div className="mt-1 min-h-0 flex-1">
          {selectedMonthDays.length?<ResponsiveContainer width="100%" height="100%"><ComposedChart data={selectedMonthDays.map(d=>{const cost=metrics.dailyProfitability.find(x=>x.sourceDate===d.sourceDate);return {...d,cogs:cost?.cogs??null,grossProfit:cost?.grossProfit??null,label:new Date(d.sourceDate+"T12:00:00").toLocaleDateString("tr-TR",{day:"2-digit",month:"short"})};})} margin={{top:8,right:8,left:0,bottom:0}}><CartesianGrid stroke="#e8edf3" strokeDasharray="3 3" vertical={false}/><XAxis dataKey="label" tick={{fontSize:9,fill:"#64748b"}} tickLine={false} axisLine={{stroke:"#cbd5e1"}}/><YAxis tickFormatter={compact} tick={{fontSize:9,fill:"#64748b"}} tickLine={false} axisLine={false} width={42}/><Tooltip wrapperStyle={{zIndex:20,pointerEvents:"none"}} contentStyle={{fontSize:10,padding:"5px 7px",borderRadius:7,border:"1px solid #dbe2ea",maxWidth:180}} formatter={(v:any,n:any)=>[v==null?"Maliyet eşleşmesi yok":money(Number(v)),n]}/><Legend wrapperStyle={{fontSize:9,paddingTop:2}} formatter={(v:any)=>v}/><Bar dataKey="revenueExVat" name="Net satış" fill="#172033" radius={[3,3,0,0]} maxBarSize={18}/><Bar dataKey="cogs" name="Ürün maliyeti" fill="#94a3b8" radius={[3,3,0,0]} maxBarSize={18}/><Line dataKey="grossProfit" name="Brüt kâr" type="monotone" stroke="#159b62" strokeWidth={2} dot={false}/></ComposedChart></ResponsiveContainer>:<div className="grid h-full place-items-center text-xs text-slate-400">Seçilen ay için günlük satış raporu bulunamadı.</div>}
        </div>
      </article>
      <article className="dashboard-panel min-h-0">
        <div className="flex items-center justify-between"><div><h2 className="dashboard-title">Aylık Karşılaştırma</h2><p className="dashboard-subtitle">KDV hariç net satış · kayıtlı dönemler</p></div><span className="text-[9px] text-slate-400">TL</span></div>
        <div className="mt-1 min-h-0 flex-1">
          {monthlyComparisonData.length?<ResponsiveContainer width="100%" height="100%"><ComposedChart data={monthlyComparisonData} barGap={3} barCategoryGap="42%" margin={{top:8,right:4,left:0,bottom:0}}><CartesianGrid stroke="#e8edf3" strokeDasharray="3 3" vertical={false}/><XAxis dataKey="label" tick={{fontSize:9,fill:"#64748b"}} tickLine={false} axisLine={{stroke:"#cbd5e1"}}/><YAxis tickFormatter={compact} tick={{fontSize:9,fill:"#64748b"}} tickLine={false} axisLine={false} width={42}/><Tooltip formatter={(v:any)=>money(Number(v))}/><Bar dataKey="netSales" name="Net Satış" fill="#172033" radius={[2,2,0,0]} maxBarSize={16}/><Bar dataKey="grossProfit" name="Brüt Kâr" fill="#16a34a" radius={[2,2,0,0]} maxBarSize={16}/></ComposedChart></ResponsiveContainer>:<div className="grid h-full place-items-center text-xs text-slate-400">Karşılaştırılabilir dönem verisi yok.</div>}
        </div>
      </article>
    </section>

    <section className="grid min-h-0 grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
      <article className="dashboard-panel min-h-0">
        <div className="flex items-center justify-between gap-2"><div><h2 className="dashboard-title">En Çok Satan Ürünler</h2><p className="dashboard-subtitle">Yüklenmiş satış verilerinin tamamı · satılan adet sayısına göre sıralı</p></div><span className="text-[9px] text-slate-400">Adet / Net satış</span></div>
        <div className="dashboard-scroll-region mt-1 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
          {metrics.top.length?<table className="w-full table-fixed text-[11px]"><thead className="sticky top-0 z-10 border-b border-slate-200 bg-white text-slate-600"><tr><th className="w-8 py-2 text-left">#</th><th className="py-2 text-left">Ürün Adı</th><th className="w-16 py-2 text-right">Adet</th><th className="w-24 py-2 text-right">Net Satış</th><th className="w-24 py-2 text-right">Brüt Kâr</th><th className="w-16 py-2 text-right">Kâr %</th></tr></thead><tbody>{metrics.topWithCost.map((p,i)=><tr key={p.key} className="border-b border-slate-100 last:border-0"><td className="py-2 text-slate-400">{i+1}</td><td className="truncate py-2 pr-2 font-medium text-slate-800" title={p.name}>{p.name}</td><td className="py-2 text-right tabular-nums">{number(p.qty)}</td><td className="py-2 text-right font-semibold tabular-nums">{money(p.revenueExVat)}</td><td className="py-2 text-right font-semibold tabular-nums">{p.grossProfit==null?"—":money(p.grossProfit)}</td><td className="py-2 text-right font-semibold tabular-nums">{p.margin==null?"—":`%${number(p.margin)}`}</td></tr>)}</tbody></table>:<div className="grid h-full place-items-center text-xs text-slate-400">Ürün satış verisi bulunamadı.</div>}
        </div>
      </article>
      <article className="dashboard-panel min-h-0">
        <div><h2 className="dashboard-title">Ürün Kategorileri</h2><p className="dashboard-subtitle">Ürün adından sınıflandırılmış satış adedi dağılımı</p></div>
        {categoryBreakdown.length ? <div className="mt-1 flex min-h-0 flex-1 items-center gap-2">
          <div className="h-full min-w-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={categoryBreakdown} dataKey="qty" nameKey="category" innerRadius="52%" outerRadius="78%" paddingAngle={2}>
                {categoryBreakdown.map((item,i)=><Cell key={item.category} fill={colors[i%colors.length]}/>)}
              </Pie><Tooltip formatter={(value:any)=>[number(Number(value))+" adet","Satış adedi"]}/></PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-[48%] space-y-1 overflow-hidden">
            {categoryBreakdown.map((item,i)=><div key={item.category} className="flex items-center justify-between gap-1 text-[10px]">
              <span className="flex min-w-0 items-center gap-1"><i className="h-2 w-2 shrink-0 rounded-full" style={{backgroundColor:colors[i%colors.length]}}/><span className="truncate">{item.category}</span></span>
              <strong className="shrink-0 tabular-nums">{Math.round(metrics.units>0?item.qty/metrics.units*100:0)}%</strong>
            </div>)}
          </div>
        </div> : <div className="grid min-h-0 flex-1 place-items-center text-xs text-slate-400">Ürün kategorisi verisi bulunamadı.</div>}
      </article>

    </section>

    <section className="grid min-h-0 grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,.95fr)_minmax(0,.85fr)]">
      <article className="dashboard-panel min-h-0">
        <div className="flex items-center justify-between"><h2 className="dashboard-title">Günlük Satış Özeti</h2><span className="text-[10px] text-slate-400">Seçilen ay · yeni tarih üstte</span></div>
        <div className="mt-1 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"><table className="w-full table-fixed text-[11px]"><thead className="sticky top-0 z-10 border-b border-slate-200 bg-white text-slate-600"><tr><th className="w-[22%] py-2 text-left">Tarih</th><th className="py-2 text-right">Net Satış</th><th className="py-2 text-right">Satış</th><th className="w-12 py-2 text-right">Adet</th><th className="py-2 text-right">Ürün Maliyeti</th><th className="py-2 text-right">Brüt Kâr</th><th className="py-2 text-right">Net Kâr</th></tr></thead><tbody>{latestDays.map(day=><tr key={day.sourceDate} className="border-b border-slate-100 last:border-0"><td className="py-2 text-slate-600">{new Date(day.sourceDate+"T12:00:00").toLocaleDateString("tr-TR")}</td><td className="py-2 text-right font-medium tabular-nums">{money(day.revenueExVat)}</td><td className="py-2 text-right tabular-nums">{money(day.revenue)}</td><td className="py-2 text-right tabular-nums">{number(day.units)}</td><td className="py-2 text-right tabular-nums">{metrics.dailyProfitability.find(x=>x.sourceDate===day.sourceDate)?money(metrics.dailyProfitability.find(x=>x.sourceDate===day.sourceDate)!.cogs):"—"}</td><td className="py-2 text-right font-semibold tabular-nums">{metrics.dailyProfitability.find(x=>x.sourceDate===day.sourceDate)?money(metrics.dailyProfitability.find(x=>x.sourceDate===day.sourceDate)!.grossProfit):"—"}</td><td className="py-2 text-right text-slate-400" title="Günlük gider dağılımı bulunmadığından hesaplanmıyor">—</td></tr>)}</tbody></table>{!latestDays.length?<p className="py-4 text-center text-[11px] text-slate-400">Günlük kayıt bulunamadı.</p>:null}</div>
      </article>
      <article className="dashboard-panel min-h-0">
        <div><h2 className="dashboard-title">İşletme Giderleri</h2><p className="dashboard-subtitle">{selectedMonthName} · kayıtlı gider kategorileri</p></div>
        {monthlyExpenseBreakdown.length?<div className="mt-1 flex min-h-0 flex-1 items-center gap-2"><div className="h-full min-w-0 flex-1"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={monthlyExpenseBreakdown} dataKey="amount" nameKey="category" innerRadius="52%" outerRadius="78%" paddingAngle={2}>{monthlyExpenseBreakdown.map((x,i)=><Cell key={x.category} fill={colors[i%colors.length]}/>)}</Pie><Tooltip formatter={(v:any)=>money(Number(v))}/></PieChart></ResponsiveContainer></div><div className="w-[48%] space-y-1 overflow-hidden">{monthlyExpenseBreakdown.slice(0,5).map((x,i)=><div key={x.category} className="flex items-center justify-between gap-1 text-[9px]"><span className="flex min-w-0 items-center gap-1"><i className="h-2 w-2 shrink-0 rounded-full" style={{backgroundColor:colors[i%colors.length]}}/><span className="truncate">{x.category}</span></span><strong className="shrink-0 tabular-nums">{money(x.amount)}</strong></div>)}</div></div>:<div className="grid min-h-0 flex-1 place-items-center text-xs text-slate-400">{expenseError??"Bu ay için gider kaydı bulunmuyor."}</div>}
        <div className="flex items-center justify-between border-t border-slate-100 pt-1.5 text-[10px]"><span className="text-slate-500">Toplam gider</span><strong className="tabular-nums">{monthlyExpenseTotal==null?"—":money(monthlyExpenseTotal)}</strong></div>
      </article>
          <article className="dashboard-panel min-h-0">
        <div className="flex items-center justify-between gap-1"><div><h2 className="dashboard-title">Hedef ve Gerçekleşme</h2><p className="dashboard-subtitle">{new Date(targetMonth+"-01T12:00:00").toLocaleDateString("tr-TR",{month:"long",year:"numeric"})}</p></div><div className="flex items-center gap-1"><Input aria-label="Hedef ayı" type="month" value={targetMonth} onChange={e=>{setTargetMonth(e.target.value);setTargetNotice(null);}} className="h-7 w-[112px] border-slate-200 px-1 text-[9px]"/><Target className="h-4 w-4 shrink-0 text-slate-500"/></div></div>
        <div className="mt-1 flex min-h-0 flex-1 items-center gap-2">
          <div className="relative grid h-[76px] w-[76px] shrink-0 place-items-center rounded-full" style={{background:`conic-gradient(#2879df ${targetProgress??0}%, #e5e7eb 0)`}}><div className="grid h-[60px] w-[60px] place-items-center rounded-full bg-white text-[17px] font-bold tabular-nums">{targetProgress==null?"—":Math.round(targetProgress)+"%"}</div></div>
          <div className="min-w-0 flex-1"><div className="text-[9px] text-slate-500">Gerçekleşen</div><div className="truncate text-[12px] font-bold tabular-nums">{money(targetMonthActual)}</div><div className="mt-1 text-[9px] text-slate-500">Aylık hedef</div><div className="truncate text-[11px] font-semibold tabular-nums">{target&&target>0?money(target):"Belirlenmedi"}</div></div>
        </div>
        <div className="flex items-center gap-1 border-t border-slate-100 pt-1.5"><Input aria-label="Aylık ciro hedefi" inputMode="decimal" value={targetText} onChange={e=>setTargetText(e.target.value)} placeholder="Hedef TL" className="h-7 min-w-0 px-2 text-[10px]"/><Button size="sm" className="h-7 shrink-0 px-2 text-[9px]" onClick={()=>void saveMonthlyTarget()} disabled={targetSaving||!targetText.trim()}>{targetSaving?"…":"Kaydet"}</Button></div>
        {targetError?<p role="alert" className="text-[9px] text-red-600">{targetError}</p>:null}{targetNotice?<p role="status" className="text-[9px] text-emerald-700">{targetNotice}</p>:null}
      </article>
    </section>






    {aiOpen?<div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 p-3 sm:p-5" role="dialog" aria-modal="true" aria-label="Kartalyuvası AI işletme danışmanı">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2"><div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><Sparkles className="h-5 w-5"/></div><div><h2 className="text-sm font-semibold text-slate-900">Kartalyuvası AI Danışman</h2><p className="text-[10px] text-slate-500">İFM işletme analizi · Türkçe</p></div></div>
          <div className="flex items-center gap-2"><Button variant="outline" size="sm" onClick={()=>{setAiMessages([]);setAiError(null);setAiText("");}} className="h-8 text-[11px]">Yeni sohbet</Button><Button variant="outline" size="sm" onClick={()=>{discardAudioRef.current = true;mediaRecorderRef.current?.stop?.();if("speechSynthesis" in window)window.speechSynthesis.cancel();setAiSpeaking(false);setAiOpen(false);}} className="h-8 text-[11px]">Kapat</Button></div>
        </div>
        <div className="min-h-[260px] flex-1 space-y-3 overflow-y-auto bg-slate-50/70 p-4">
          {!aiMessages.length?<div className="mx-auto max-w-lg py-4 text-center"><div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100 text-violet-700"><Sparkles className="h-6 w-6"/></div><h3 className="text-base font-semibold text-slate-900">Mağazanızın performansını birlikte inceleyelim</h3><p className="mt-1 text-xs leading-5 text-slate-500">Satış, maliyet, stok, gider ve ciro hedefleri hakkında soru sorabilir veya hazır analiz başlatabilirsiniz.</p><div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {["Seçili ayın performansını analiz et","En önemli 3 satış ve kârlılık riskimiz nedir?","Stokta bekleyen ve satış fırsatı olan ürünleri incele","Ciro hedefimize ulaşmak için ne yapmalıyız?"].map((prompt)=><button key={prompt} type="button" onClick={()=>void askAi(prompt)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-700 transition hover:border-violet-300 hover:bg-violet-50">{prompt}</button>)}
          </div></div>:aiMessages.map((message,index)=><div key={index} className={message.role==="user"?"ml-auto max-w-[88%] rounded-2xl rounded-br-sm bg-slate-900 px-3 py-2.5 text-sm text-white":"mr-auto max-w-[94%] rounded-2xl rounded-bl-sm border border-slate-200 bg-white px-3 py-2.5 text-sm leading-6 text-slate-700"}><div className="mb-1 text-[9px] font-semibold uppercase tracking-wide opacity-60">{message.role==="user"?"Siz":"AI Danışman"}</div><div className="whitespace-pre-wrap">{message.text}</div>{message.role==="assistant"?<div className="mt-2 flex justify-end"><Button variant="ghost" size="sm" onClick={()=>speakAiText(message.text)} className="h-7 gap-1 px-2 text-[10px]"><Volume2 className="h-3.5 w-3.5"/> {aiSpeaking?"Tekrar dinle":"Sesli dinle"}</Button></div>:null}</div>)}
          {aiLoading?<div className="mr-auto flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin"/>Veriler inceleniyor ve yanıt hazırlanıyor…</div>:null}
          {aiError?<div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{aiError}</div>:null}
        </div>
        <form onSubmit={(event)=>{event.preventDefault();void askAi(aiInput);}} className="border-t border-slate-200 bg-white p-3">
          <div className="flex items-end gap-2"><textarea value={aiInput} onChange={(event)=>setAiInput(event.target.value)} onKeyDown={(event)=>{if(event.key==="Enter"&&!event.shiftKey){event.preventDefault();void askAi(aiInput);}}} placeholder="Örn. Kasım ayında kârlılığımızı nasıl artırabiliriz?" rows={2} className="max-h-28 min-h-10 flex-1 resize-y rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" />
            <Button type="button" variant="outline" size="icon" onClick={aiListening?()=>{if(mediaRecorderRef.current?.state==="recording")mediaRecorderRef.current.stop();}:()=>{void startAiVoiceInput();}} className={`h-10 w-10 shrink-0 ${aiListening?"border-red-300 bg-red-50 text-red-600":""}`} aria-label={aiListening?"Ses kaydını durdur":"Sesli soru sor"}><Mic className="h-4 w-4"/></Button>
            <Button type="submit" disabled={aiLoading||!aiInput.trim()} className="h-10 shrink-0 px-3"><Sparkles className="h-4 w-4"/> Gönder</Button>
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[9px] text-slate-400"><span>{aiListening?"Kaydediliyor… Bitirmek için mikrofon düğmesine tekrar basın.":"Enter ile gönder · Shift+Enter yeni satır"}</span><span>Yanıtlar kayıt değiştirmez</span></div>
        </form>
      </div>
    </div>:null}
  </div>;
};
export default Dashboard;